import {
  Box,
  Button,
  ColumnLayout,
  Container,
  ExpandableSection,
  NonCancelableCustomEvent,
  PropertyFilter,
  PropertyFilterProps,
  Select,
  SelectProps,
  SpaceBetween,
} from '@amzn/awsui-components-core';
import {
  BarRaiserFilter,
  DraftContentQueryConditions,
  TechDomain,
  TechDomainDisplayNames,
  WorkflowAttributesQueryConditon,
} from '@amzn/awscat-pattern-library-service-model';
import React, { useState, useEffect, useContext } from 'react';
import { MplServiceClientFactory } from '../../../api/MplServiceClientFactory';
import { localLog } from '../../../loggers/Logger';
import { DefaultI18nStrings } from '../../workflow-viewer/ContentWorkflowTableFilters';
import { v4 as uuid } from 'uuid';
import { pgsFilter } from '../../../api/ContentUtil';
import { buildTableItems } from '../../workflow-viewer/ContentWorkflowTableDefinitions';
import { ContentQueryContext } from './ContentQueryContext';
import { DateTime } from 'luxon';
import { WorkflowTableIdentity } from '../../../common/CookieRegistry';
import { allowedStatesMap, isNonPendingPublisherReview } from '../../utils/BarRaiser';

interface ContentFilterConditionProps {
  id: string;
  query: PropertyFilterProps.Query;
  showRemoveButton: boolean;
  tableId?: WorkflowTableIdentity;
  onChange: (g: QueryGroup) => void;
  onRemove: (id: string) => void;
}

function ContentFilterCondition(props: ContentFilterConditionProps) {
  const { tableId } = props;
  const filteringProperties: PropertyFilterProps.FilteringProperty[] = [
    {
      key: 'mainTechDomain',
      propertyLabel: 'Main Technology',
      groupValuesLabel: 'Technologies',
      operators: ['='],
      defaultOperator: '=',
      group: 'contentMetadataAttributes',
    },
    {
      key: 'techDomains',
      propertyLabel: 'Additional Technologies',
      groupValuesLabel: 'Technologies',
      operators: ['='],
      defaultOperator: '=',
      group: 'contentMetadataAttributes',
    },
    ...(tableId
      ? [
          {
            key: 'workflowState',
            propertyLabel: 'Workflow State',
            groupValuesLabel: 'Workflow State',
            operators: ['='],
            defaultOperator: '=',
            group: 'workflowStateAttributes',
          },
        ]
      : []),
  ];

  const customGroupsText = [
    {
      properties: 'Content Metadata Attributes',
      values: 'Content Metadata Attributes',
      group: 'contentMetadataAttributes',
    },
    ...(tableId
      ? [
          {
            properties: 'Workflow State Attributes',
            values: 'Workflow State Attributes',
            group: 'workflowStateAttributes',
          },
        ]
      : []),
  ];

  // @ts-expect-error TS2345 - strict migration
  const allowedStates = allowedStatesMap.get(tableId) || [];
  // @ts-expect-error TS2322 - strict migration
  const filteringOptions: PropertyFilterProps.FilteringOption[] = [
    ...Object.keys(TechDomain).map((t) => ({
      propertyKey: 'mainTechDomain',
      // @ts-expect-error TS7053 - strict migration
      value: TechDomainDisplayNames.get(TechDomain[t]),
    })),
    ...Object.keys(TechDomain).map((t) => ({
      propertyKey: 'techDomains',
      // @ts-expect-error TS7053 - strict migration
      value: TechDomainDisplayNames.get(TechDomain[t]),
    })),
    ...(allowedStates.length > 0
      ? allowedStates.map((state) => ({ propertyKey: 'workflowState', value: state }))
      : []),
  ];
  localLog.debug({ filteringOptions });

  const handleFilterChange = (e: NonCancelableCustomEvent<PropertyFilterProps.Query>) => {
    props.onChange({ id: props.id, query: e.detail });
  };

  const handleRemoveClick = () => {
    props.onRemove(props.id);
  };

  const deleteButton = props.showRemoveButton ? (
    <Button iconName="close" onClick={handleRemoveClick}>
      Remove condition
    </Button>
  ) : null;

  return (
    <Container>
      <SpaceBetween size="s">
        <PropertyFilter
          i18nStrings={DefaultI18nStrings}
          onChange={handleFilterChange}
          query={props.query}
          customGroupsText={customGroupsText}
          filteringProperties={filteringProperties}
          filteringOptions={filteringOptions}
          disableFreeTextFiltering
        />
        {deleteButton}
      </SpaceBetween>
    </Container>
  );
}

export interface QueryGroup {
  id: string;
  query: PropertyFilterProps.Query;
}

interface ServerSideQueryContentsProviderProps {
  queryGroups?: QueryGroup[];
  queryGroupOperation?: PropertyFilterProps.JoinOperation;
  tableId?: WorkflowTableIdentity;
}

export function ServerSideQueryContentsProvider(props: ServerSideQueryContentsProviderProps) {
  const { tableId } = props;
  const blankQuery: () => QueryGroup = () => ({
    id: uuid(),
    query: {
      tokens: [],
      operation: 'and',
    },
  });

  const [queryGroups, setQueryGroups] = useState(props.queryGroups || [blankQuery()]);
  const [queryGroupOperation, setQueryGroupOperation] = useState<PropertyFilterProps.JoinOperation>(
    props.queryGroupOperation || 'and',
  );

  const { queryState, dispatchQueryState } = useContext(ContentQueryContext);

  useEffect(() => {
    setQueryGroups(props.queryGroups || [blankQuery()]);
    fetchItems(props.queryGroups);
  }, [props.queryGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchItems(queryGroups);
  }, [queryState.queryTimestamp]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddConditionClick = () => {
    setQueryGroups([...queryGroups, blankQuery()]);
  };

  const handleQueryGroupChange = (queryGroup: QueryGroup) => {
    const idx = queryGroups.findIndex((g) => g.id === queryGroup.id);
    queryGroups[idx] = queryGroup;
    setQueryGroups([...queryGroups]);
  };

  const handleQueryGroupDelete = (id: string) => {
    const idx = queryGroups.findIndex((g) => g.id === id);
    queryGroups.splice(idx, 1);
    setQueryGroups([...queryGroups]);
  };

  const handleOperationChange = (e: NonCancelableCustomEvent<SelectProps.ChangeDetail>) =>
    setQueryGroupOperation(e.detail.selectedOption.value as PropertyFilterProps.JoinOperation);

  // @ts-expect-error TS7006 - strict migration
  const fetchItems = async (queryGroup) => {
    try {
      dispatchQueryState({ isExecutingQuery: true });
      const mplServiceClient = MplServiceClientFactory.create();

      const queryConditions: DraftContentQueryConditions[] = [];
      const selectedMainTechDomains: string[] = [];

      for (const qg of queryGroup) {
        const queryCondition = new DraftContentQueryConditions({
          contentMetadataAttributes: [],
          queryConditionOperation: qg.query.operation,
        });
        const workflowQuery = new WorkflowAttributesQueryConditon({
          queryConditionOperation: 'or',
        });
        for (const t of qg.query.tokens) {
          if (t.propertyKey === 'mainTechDomain') {
            queryCondition.contentMetadataAttributes.push({
              name: 'MainTechDomain',
              value: t.value,
            });
            selectedMainTechDomains.push(t.value);
          }
          if (t.propertyKey === 'techDomains') {
            queryCondition.contentMetadataAttributes.push({
              name: 'TechDomain',
              value: t.value,
            });
          }
          if (t.propertyKey === 'workflowState') {
            workflowQuery.attributes.push({
              name: 'state',
              value: t.value,
            });
          }
        }
        queryCondition.draftWorkflowAttributes = workflowQuery;
        queryConditions.push(queryCondition);
      }

      const [{ workflows }, allSharedContents] = await Promise.all([
        mplServiceClient.queryContent(
          queryConditions,
          queryGroupOperation,
          // @ts-expect-error TS2538 - strict migration
          BarRaiserFilter[tableId?.toString()],
        ),
        mplServiceClient.listAllSharedContents(),
      ]);

      const contentCoauthorMap = new Map<string, string[]>(Object.entries(allSharedContents));
      const filteredWorkflows = pgsFilter(workflows);

      // Adding domain specific filter, the reason is because that there are some legacy data in content resource table.
      // when the content's main tech domain is changed in different workflows, the main tech domain in resource tables have the records
      // for the old tech domain. As a result, it would be shown on the wrong dashboard.
      // But the template metadata for a content contains the latest main tech domain. That is why a new domain specific filter is needed
      const domainFilteredWorkflows =
        selectedMainTechDomains.length > 0
          ? filteredWorkflows.filter((w) =>
              selectedMainTechDomains.includes(w.draftTemplateMetadata?.mainTechDomain),
            )
          : filteredWorkflows;

      // Show only pending publisher reviews in the bar raiser queue. Filtered client-side
      // because reviewStatus is a nested attribute the content query cannot filter on.
      const reviewFilteredWorkflows =
        tableId === WorkflowTableIdentity.DraftBarRaiserDashboard
          ? domainFilteredWorkflows.filter((w) => !isNonPendingPublisherReview(w))
          : domainFilteredWorkflows;

      const tableItems = buildTableItems(reviewFilteredWorkflows, { contentCoauthorMap });
      dispatchQueryState({
        isExecutingQuery: false,
        queryResult: tableItems,
      });
    } catch (e) {
      // @ts-expect-error TS18046 - strict migration
      let errorMessage = e.message;
      // @ts-expect-error TS18046 - strict migration
      if (e.response) {
        // @ts-expect-error TS18046 - strict migration
        errorMessage = e.response.data.message;
      }
      dispatchQueryState({
        isExecutingQuery: false,
        queryResult: [],
        queryError: errorMessage,
      });
    }
  };

  const handleRunQueryClick = () => {
    dispatchQueryState({
      queryTimestamp: DateTime.now(),
      isExecutingQuery: true,
    });
  };

  const queryFilters = queryGroups.map((q, idx) => {
    const queryGroupOperationSelector =
      idx > 0 ? (
        <ColumnLayout columns={12}>
          <Select
            selectedOption={{ value: queryGroupOperation }}
            options={[{ value: 'and' }, { value: 'or' }]}
            onChange={handleOperationChange}
            ariaRequired
          />
        </ColumnLayout>
      ) : null;

    return (
      <SpaceBetween key={q.id} size="s">
        {queryGroupOperationSelector}
        <ContentFilterCondition
          tableId={tableId}
          id={q.id}
          query={q.query}
          showRemoveButton={idx !== 0}
          onChange={handleQueryGroupChange}
          onRemove={handleQueryGroupDelete}
        />
      </SpaceBetween>
    );
  });

  const addNewConditionButton =
    queryGroups.length < 3 ? (
      <Button onClick={handleAddConditionClick}>Add new condition</Button>
    ) : null;

  return (
    <SpaceBetween size="s">
      <ExpandableSection header="Query conditions">
        <SpaceBetween size="s">
          {queryFilters}
          <Box float="right">
            <SpaceBetween direction="horizontal" size="s">
              {addNewConditionButton}
              <Button
                variant="primary"
                iconName="refresh"
                loading={queryState.isExecutingQuery}
                onClick={handleRunQueryClick}
              >
                Update Results
              </Button>
            </SpaceBetween>
          </Box>
        </SpaceBetween>
      </ExpandableSection>
    </SpaceBetween>
  );
}
